import type { ActivityOption, CollabInfoDto } from '@/api/generated/types.gen';
import { ChevronRight, FileText, PlayCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Markdown } from '@/shared/components/markdown/Markdown';
import { termenExpirat, ziSiLuna } from '@/domains/lessons/features/practical/practical-evidence';
import { grupeazaActivitati, type TipColab } from '../utils/tipuri-colab';
import { EtichetaTip, ICONITA_TIP, NUANTA_TIP } from './EtichetaTip';

interface Props { info: CollabInfoDto; onChoose: (lessonId: string, tip: TipColab) => void }

/** Ce are activitatea, sub descriere: film, fisa, termen. Text + iconita, nu doar iconita. */
function Indicii({ a }: { a: ActivityOption }) {
    const zi = ziSiLuna(a.deadline);
    const expirat = termenExpirat(a.deadline);
    if (!a.areVideo && !a.arePdfExercitii && !zi) return null;
    return (
        <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-dark/70">
            {a.areVideo && <span className="inline-flex items-center gap-1"><PlayCircle className="size-3.5" aria-hidden /> Film</span>}
            {a.arePdfExercitii && <span className="inline-flex items-center gap-1"><FileText className="size-3.5" aria-hidden /> Fișă PDF</span>}
            {zi && <span className={cn(expirat && 'font-semibold text-warning')}>{expirat ? 'Termen expirat' : `Până pe ${zi}`}</span>}
        </span>
    );
}

/** Derulare lina doar pentru cine n-a cerut mai putina miscare. */
function sariLaSectiune(tip: TipColab) {
    const lin = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(`colab-sectiune-${tip}-zona`)?.scrollIntoView({ behavior: lin ? 'smooth' : 'auto', block: 'start' });
}

/**
 * Pasul 1: ce se raporteaza. Doua sectiuni mereu vizibile - activitati practice si exercitii cu
 * clasa - fiecare cu titlu, iconita si o propozitie despre ce se trimite; pe ecran lat stau una
 * langa alta. Pana pe 2026-10-05 era o singura lista, intitulata „Activitate practică", in care
 * exercitiile cu clasa nu se deosebeau de nimic.
 *
 * Cardurile cresc cu continutul: `min-h-11` (44px) e doar pragul de tap, nu o inaltime fixa.
 */
export function StepChooseActivity({ info, onChoose }: Props) {
    const grupe = grupeazaActivitati(info.activities);
    return (
        <div className="flex flex-col gap-4">
            <p className="text-fluid-base">Ce ați făcut cu elevii?</p>
            {/* Doar pe telefon, unde sectiunile stau una sub alta: activitatile practice ocupau peste
                cinci ecrane inaintea primului exercitiu cu clasa, deci a doua sectiune nu se vedea
                (masurat pe PROD, 2026-10-06). Pe ecran lat stau alaturi si saltul n-are rost. */}
            <nav aria-label="Sari la secțiune" className="grid grid-cols-2 gap-2 md:hidden" data-colab-salt>
                {grupe.map((g) => {
                    const Iconita = ICONITA_TIP[g.tip];
                    return (
                        <button
                            key={g.tip}
                            type="button"
                            onClick={() => sariLaSectiune(g.tip)}
                            className={cn(
                                'flex min-h-11 items-center gap-2 rounded-lg border border-l-4 border-input bg-white px-3 py-2 text-left text-sm font-semibold leading-tight active:bg-lilac/30',
                                NUANTA_TIP[g.tip].accent
                            )}
                        >
                            <Iconita className="size-4 shrink-0" aria-hidden />
                            <span className="min-w-0">
                                {g.titluSectiune} <span className="font-normal text-dark/70">({g.activitati.length})</span>
                            </span>
                        </button>
                    );
                })}
            </nav>
            <div className="grid gap-6 md:grid-cols-2 md:gap-8">
                {grupe.map((g) => {
                    const Iconita = ICONITA_TIP[g.tip];
                    const idTitlu = `colab-sectiune-${g.tip}`;
                    return (
                        <section key={g.tip} id={idTitlu + '-zona'} aria-labelledby={idTitlu} data-colab-sectiune={g.tip} className="flex scroll-mt-4 flex-col gap-3">
                            <header className="flex items-start gap-3">
                                <span className={cn('flex size-11 shrink-0 items-center justify-center rounded-full', NUANTA_TIP[g.tip].iconita)}>
                                    <Iconita className="size-5" aria-hidden />
                                </span>
                                <div className="min-w-0">
                                    <h2 id={idTitlu} className="text-fluid-lg font-semibold leading-tight">{g.titluSectiune}</h2>
                                    <p className="text-sm text-pretty text-dark/70">{g.explicatie}</p>
                                </div>
                            </header>
                            {g.activitati.length === 0 ? (
                                <p className="rounded-lg border border-dashed border-input px-4 py-3 text-sm text-dark/70">{g.gol}</p>
                            ) : (
                                <ul className="flex flex-col gap-2">
                                    {g.activitati.map((a) => (
                                        <li key={a.id}>
                                            <button
                                                type="button"
                                                onClick={() => onChoose(a.id as string, g.tip)}
                                                className={cn(
                                                    'flex min-h-11 w-full items-center gap-3 rounded-lg border border-l-4 border-input bg-white px-4 py-3 text-left active:bg-lilac/30',
                                                    NUANTA_TIP[g.tip].accent
                                                )}
                                            >
                                                {a.image && <img src={a.image} alt="" loading="lazy" className="size-12 shrink-0 rounded object-cover" />}
                                                <span className="min-w-0 flex-1">
                                                    <EtichetaTip tip={g.tip} className="mb-1" />
                                                    {/* Impachetare, nu trunchiere: `truncate` + `title` ascundea 54%
                                                        din titlu la 360 (S1, constatarea 28) in spatele unui tooltip
                                                        care nu exista pe atingere. */}
                                                    <span className="block break-words font-medium">{a.title}</span>
                                                    {/* Descrierea intreaga e pe ecranul urmator; aici doar inceputul. */}
                                                    <Markdown mode="inline" source={a.description} className="line-clamp-2 text-sm text-dark/60" />
                                                    <Indicii a={a} />
                                                </span>
                                                <ChevronRight className="size-5 shrink-0 text-dark/40" aria-hidden />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    );
                })}
            </div>
        </div>
    );
}
